/**
 * 留言管理页面 - 销售角色
 */
import { useState, useEffect, useMemo } from 'react';
import { Loader2, Search } from 'lucide-react';
import { api, getErrorMessage } from '../services/api';
import { useToast } from '../components/Toast';

export default function MessageManagement() {
  const { showToast } = useToast();
  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'unreplied' | 'replied' | 'all'>('unreplied');
  const [replyText, setReplyText] = useState<Record<number, string>>({});
  const [replying, setReplying] = useState<Record<number, boolean>>({});

  const fetchMessages = async () => {
    setLoading(true);
    try { const r = await api.request<any>('/promotions/message-board'); setMessages(r.data || []); }
    catch (e) { console.error('加载留言失败:', e); setMessages([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchMessages(); }, []);

  const handleReply = async (mid: number) => {
    const text = replyText[mid]?.trim();
    if (!text) return;
    setReplying(p => ({ ...p, [mid]: true }));
    try {
      await api.request('/promotions/message-board/' + mid + '/reply', { method: 'PUT', body: JSON.stringify({ reply: text }) });
      showToast('success', '已回复');
      setReplyText(p => { const n = { ...p }; delete n[mid]; return n; });
      fetchMessages();
      window.dispatchEvent(new CustomEvent('refresh-sidebar-counts'));
    } catch (e) { showToast('error', getErrorMessage(e)); }
    finally { setReplying(p => ({ ...p, [mid]: false })); }
  };

  const handleClose = async (mid: number) => {
    try {
      await api.request('/promotions/message-board/' + mid + '/close', { method: 'PUT' });
      showToast('success', '已关闭');
      fetchMessages();
      window.dispatchEvent(new CustomEvent('refresh-sidebar-counts'));
    } catch (e) { showToast('error', getErrorMessage(e)); }
  };

  const unreplied = useMemo(() => messages.filter(m => m.status === 0), [messages]);
  const replied = useMemo(() => messages.filter(m => m.status === 1 || m.status === 2), [messages]);

  const list = tab === 'unreplied' ? unreplied : tab === 'replied' ? replied : messages;

  const filtered = search
    ? list.filter(m => m.content.includes(search) || m.user_name.includes(search))
    : list;

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700 }}>留言管理</h1>
        <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 8, padding: 3 }}>
          <button onClick={() => setTab('unreplied')} style={{
            padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '.8125rem', fontWeight: 500,
            background: tab === 'unreplied' ? '#fff' : 'transparent', color: tab === 'unreplied' ? 'var(--cinnabar-600)' : 'var(--ink-500)',
            boxShadow: tab === 'unreplied' ? '0 1px 3px rgba(0,0,0,.1)' : 'none', display: 'flex', alignItems: 'center', gap: 6
          }}>
            未回复 {unreplied.length > 0 && <span style={{ background: '#ef4444', color: '#fff', fontSize: '.6875rem', minWidth: 20, height: 20, borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>{unreplied.length}</span>}
          </button>
          <button onClick={() => setTab('replied')} style={{
            padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '.8125rem', fontWeight: 500,
            background: tab === 'replied' ? '#fff' : 'transparent', color: tab === 'replied' ? 'var(--cinnabar-600)' : 'var(--ink-500)',
            boxShadow: tab === 'replied' ? '0 1px 3px rgba(0,0,0,.1)' : 'none'
          }}>已回复</button>
        </div>
      </div>

      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid var(--color-border)', borderRadius: 8, padding: '6px 12px', maxWidth: 320 }}>
        <Search size={16} style={{ color: 'var(--ink-400)' }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="搜索留言内容或用户..."
          style={{ border: 'none', outline: 'none', flex: 1, fontSize: '.8125rem', background: 'transparent' }} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--ink-400)' }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--ink-400)', fontSize: '.875rem' }}>
          {tab === 'unreplied' ? '暂无未回复留言 🎉' : '暂无留言'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(m => (
            <div key={m.id} style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 10, padding: '16px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <strong style={{ fontSize: '.875rem' }}>{m.user_name}</strong>
                  {m.company && <span style={{ fontSize: '.75rem', color: 'var(--ink-500)' }}>{m.company}</span>}
                  {m.position && <span style={{ fontSize: '.6875rem', color: 'var(--ink-400)', background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>{m.position}</span>}
                  <span style={{ fontSize: '.6875rem', color: 'var(--ink-400)' }}>{new Date(m.create_time).toLocaleString('zh-CN')}</span>
                </div>
                <span style={{
                  fontSize: '.6875rem', padding: '2px 10px', borderRadius: 10,
                  background: m.status === 0 ? '#fef3c7' : m.status === 1 ? '#dcfce7' : '#f1f5f9',
                  color: m.status === 0 ? '#92400e' : m.status === 1 ? '#166534' : '#64748b'
                }}>{['未处理', '已回复', '已关闭'][m.status]}</span>
              </div>
              <div style={{ fontSize: '.8125rem', color: 'var(--ink-700)', whiteSpace: 'pre-wrap', marginBottom: 8, lineHeight: 1.6 }}>{m.content}</div>
              {m.reply && (
                <div style={{ padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, fontSize: '.8125rem', marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, color: '#166534' }}>回复（{m.replied_by}）：</span>
                  <div style={{ color: 'var(--ink-600)', marginTop: 4, whiteSpace: 'pre-wrap' }}>{m.reply}</div>
                </div>
              )}
              {m.status === 0 && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <textarea value={replyText[m.id] || ''} onChange={e => setReplyText(p => ({ ...p, [m.id]: e.target.value }))} placeholder="输入回复..." rows={2}
                    style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--ink-200)', fontSize: '.8125rem', resize: 'none', fontFamily: 'inherit' }} />
                  <button onClick={() => handleReply(m.id)} disabled={replying[m.id] || !replyText[m.id]?.trim()}
                    style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--cinnabar-500)', color: '#fff', cursor: 'pointer', fontSize: '.8125rem', whiteSpace: 'nowrap', alignSelf: 'flex-end', opacity: (replying[m.id] || !replyText[m.id]?.trim()) ? 0.5 : 1 }}>
                    {replying[m.id] ? '回复中' : '回复'}
                  </button>
                  <button onClick={() => handleClose(m.id)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--ink-200)', background: '#fff', color: 'var(--ink-500)', cursor: 'pointer', fontSize: '.8125rem', whiteSpace: 'nowrap', alignSelf: 'flex-end' }}>关闭</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
